<template>
  <aside class="sidebar" :class="{ collapsed: isCollapsed, 'mobile-open': isMobileMenuOpen }">

    <!-- Brand -->
    <div class="sidebar-brand">
      <div class="brand-icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
      </div>
      <transition name="fade-label">
        <div v-if="!isCollapsed" class="brand-name">
          <span class="brand-text">Neuro</span><span class="brand-accent">Mancer</span>
          <span class="brand-ver">v2.0</span>
        </div>
      </transition>
      <button class="collapse-btn d-none d-md-flex" @click="toggleSidebar" :title="isCollapsed ? 'Развернуть' : 'Свернуть'">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polyline v-if="!isCollapsed" points="15 18 9 12 15 6"/>
          <polyline v-else points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>

    <!-- Navigation -->
    <div class="sidebar-nav" ref="navEl">

      <!-- Direct links -->
      <router-link to="/dashboard" class="nav-item" :class="{ active: $route.name === 'Dashboard' }" :title="isCollapsed ? 'Dashboard' : ''">
        <span class="nav-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
        </span>
        <transition name="fade-label"><span v-if="!isCollapsed" class="nav-label">Dashboard</span></transition>
      </router-link>

      <!-- ACCOUNTS group -->
      <div class="nav-group">
        <button class="nav-group-header" @click="toggle('accounts')" :class="{ 'group-active': isGroupActive('accounts') }" :title="isCollapsed ? 'Accounts' : ''">
          <span class="nav-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </span>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-group-label">Accounts</span>
          </transition>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-chevron" :class="{ open: expanded.accounts }">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </transition>
        </button>
        <transition name="slide-down">
          <div v-if="!isCollapsed && expanded.accounts" class="nav-subitems">
            <router-link to="/" class="nav-subitem" :class="{ active: $route.name === 'Accounts' }">
              <span class="sub-dot"></span>Сессии
            </router-link>
            <router-link to="/proxies" class="nav-subitem" :class="{ active: $route.name === 'Proxies' }">
              <span class="sub-dot"></span>Прокси
            </router-link>
            <router-link to="/channels" class="nav-subitem" :class="{ active: $route.name === 'Channels' }">
              <span class="sub-dot"></span>Каналы
            </router-link>
            <router-link to="/admin-channels" class="nav-subitem" :class="{ active: $route.name === 'AdminChannels' }">
              <span class="sub-dot"></span>Адмін-канали
            </router-link>
            <router-link to="/media" class="nav-subitem" :class="{ active: $route.name === 'MediaLibrary' }">
              <span class="sub-dot"></span>Медиа
            </router-link>
            <router-link to="/image-generator" class="nav-subitem" :class="{ active: $route.name === 'ImageGenerator' }">
              <span class="sub-dot"></span>Генератор
            </router-link>
            <router-link to="/prompts" class="nav-subitem" :class="{ active: $route.name === 'Prompts' }">
              <span class="sub-dot"></span>Промпты
            </router-link>
            <router-link to="/accounts/health" class="nav-subitem" :class="{ active: $route.name === 'AccountHealth' }">
              <span class="sub-dot"></span>Health Monitor
            </router-link>
          </div>
        </transition>
      </div>

      <!-- NEURO group -->
      <div class="nav-group">
        <button class="nav-group-header" @click="toggle('neuro')" :class="{ 'group-active': isGroupActive('neuro') }" :title="isCollapsed ? 'Neuro' : ''">
          <span class="nav-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><circle cx="12" cy="12" r="10"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </span>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-group-label">Neuro</span>
          </transition>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-chevron" :class="{ open: expanded.neuro }">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </transition>
        </button>
        <transition name="slide-down">
          <div v-if="!isCollapsed && expanded.neuro" class="nav-subitems">
            <router-link to="/campaigns" class="nav-subitem" :class="{ active: ['Campaigns','CampaignCreate','CampaignEdit'].includes($route.name) }">
              <span class="sub-dot"></span>Кампании
            </router-link>
            <router-link to="/action-log" class="nav-subitem" :class="{ active: $route.name === 'ActionLog' }">
              <span class="sub-dot"></span>Лог действий
            </router-link>
            <router-link to="/autopost" class="nav-subitem" :class="{ active: $route.name === 'Autopost' }">
              <span class="sub-dot"></span>Autopost
            </router-link>
            <router-link to="/repost" class="nav-subitem" :class="{ active: $route.name === 'Repost' }">
              <span class="sub-dot"></span>Репост
            </router-link>
            <router-link to="/pipelines" class="nav-subitem" :class="{ active: ['Pipelines','PipelineEditor'].includes($route.name) }">
              <span class="sub-dot"></span>Пайплайны
            </router-link>
            <router-link to="/digest" class="nav-subitem" :class="{ active: $route.name === 'Digest' }">
              <span class="sub-dot"></span>Дайджест
            </router-link>
            <router-link to="/mass-actions" class="nav-subitem" :class="{ active: $route.name === 'MassActions' }">
              <span class="sub-dot"></span>Mass Actions
            </router-link>
            <router-link to="/content-plans" class="nav-subitem" :class="{ active: ['ContentPlans','ContentPlanCreate','ContentPlanEdit'].includes($route.name) }">
              <span class="sub-dot"></span>Content Plans
            </router-link>
            <router-link to="/warmup" class="nav-subitem" :class="{ active: $route.name === 'Warmup' }">
              <span class="sub-dot"></span>Warmup
            </router-link>
          </div>
        </transition>
      </div>

      <!-- GROWTH group -->
      <div class="nav-group">
        <button class="nav-group-header" @click="toggle('growth')" :class="{ 'group-active': isGroupActive('growth') }" :title="isCollapsed ? 'Growth' : ''">
          <span class="nav-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
          </span>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-group-label">Growth</span>
          </transition>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-chevron" :class="{ open: expanded.growth }">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </transition>
        </button>
        <transition name="slide-down">
          <div v-if="!isCollapsed && expanded.growth" class="nav-subitems">
            <router-link to="/parser" class="nav-subitem" :class="{ active: $route.name === 'Parser' }">
              <span class="sub-dot"></span>User Parser
            </router-link>
            <router-link to="/personas" class="nav-subitem" :class="{ active: $route.name === 'Personas' }">
              <span class="sub-dot"></span>AI Personas
            </router-link>
            <router-link to="/parser/channels" class="nav-subitem" :class="{ active: $route.name === 'parser-channels' }">
              <span class="sub-dot"></span>Парсер каналов
            </router-link>
            <router-link to="/parser/groups" class="nav-subitem" :class="{ active: $route.name === 'parser-groups' }">
              <span class="sub-dot"></span>Парсер групп
            </router-link>
            <router-link to="/parser/history" class="nav-subitem" :class="{ active: $route.name === 'parser-history' }">
              <span class="sub-dot"></span>История
            </router-link>
          </div>
        </transition>
      </div>

      <!-- SYSTEM group -->
      <div class="nav-group">
        <button class="nav-group-header" @click="toggle('system')" :class="{ 'group-active': isGroupActive('system') }" :title="isCollapsed ? 'System' : ''">
          <span class="nav-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
          </span>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-group-label">System</span>
          </transition>
          <transition name="fade-label">
            <span v-if="!isCollapsed" class="nav-chevron" :class="{ open: expanded.system }">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </transition>
        </button>
        <transition name="slide-down">
          <div v-if="!isCollapsed && expanded.system" class="nav-subitems">
            <router-link v-if="isAdmin" to="/settings" class="nav-subitem" :class="{ active: $route.name === 'Settings' }">
              <span class="sub-dot"></span>AI Providers
            </router-link>
            <router-link v-if="isAdmin" to="/logs" class="nav-subitem" :class="{ active: $route.name === 'SystemLogs' }">
              <span class="sub-dot"></span>System Logs
            </router-link>
            <router-link v-if="isAdmin" to="/queues" class="nav-subitem" :class="{ active: $route.name === 'QueueManager' }">
              <span class="sub-dot"></span>Queues
            </router-link>
            <router-link v-if="isAdmin" to="/users" class="nav-subitem" :class="{ active: $route.name === 'Users' }">
              <span class="sub-dot"></span><i class="bi bi-people me-1"></i>Пользователи
            </router-link>
          </div>
        </transition>
      </div>

    </div><!-- /sidebar-nav -->

    <!-- Theme toggle -->
    <div class="theme-toggle-row" :class="{ 'px-0 justify-content-center': isCollapsed }">
      <button class="theme-toggle-btn" @click="toggleTheme" :title="isLight ? 'Dark mode' : 'Light mode'">
        <!-- Sun icon (shown when dark — click to switch to light) -->
        <svg v-if="!isLight" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
        <!-- Moon icon (shown when light — click to switch to dark) -->
        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      </button>
      <transition name="fade-label">
        <span v-if="!isCollapsed" class="theme-label">{{ isLight ? 'Light' : 'Dark' }}</span>
      </transition>
    </div>

    <!-- Footer -->
    <div class="sidebar-footer">
      <div class="user-row">
        <div class="user-avatar">{{ userInitial }}</div>
        <transition name="fade-label">
          <div v-if="!isCollapsed" class="user-info">
            <span class="user-name">{{ userName }}</span>
            <span class="user-role">{{ isAdmin ? 'Администратор' : 'Пользователь' }}</span>
          </div>
        </transition>
        <transition name="fade-label">
          <button v-if="!isCollapsed" class="logout-btn" @click="handleLogout" title="Выйти">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </transition>
      </div>
    </div>

  </aside>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'AdminMenu',
  props: {
    isMobileMenuOpen: { type: Boolean, default: false },
  },
  data() {
    return {
      isCollapsed: false,
      expanded: { accounts: true, neuro: false, growth: false, system: false },
      isLight: false,
    };
  },
  computed: {
    ...mapGetters(['user', 'isAuthenticated', 'isAdmin']),
    userName()   { return this.user?.name || 'Admin'; },
    userInitial(){ return (this.user?.name || 'A')[0].toUpperCase(); },
  },
  watch: {
    '$route'(to) { this.autoExpand(to.name); },
  },
  mounted() {
    this.autoExpand(this.$route.name);
    this.isLight = localStorage.getItem('nc-theme') === 'light';
    this.applyTheme();
  },
  methods: {
    ...mapActions(['logout']),
    toggleSidebar() { this.isCollapsed = !this.isCollapsed; },
    toggle(key) {
      if (!this.isCollapsed) this.expanded[key] = !this.expanded[key];
    },
    autoExpand(name) {
      const map = {
        accounts: ['Accounts','Proxies','Channels','AdminChannels','MediaLibrary','AccountHealth','ImageGenerator'],
        neuro:    ['Campaigns','CampaignCreate','CampaignEdit','ActionLog','Autopost','Repost','Digest','Warmup','ContentPlans','ContentPlanCreate','ContentPlanEdit','MassActions'],
        growth:   ['Parser','Personas','ChannelSearch','parser-channels','parser-groups','parser-history'],
        system:   ['Settings','SystemLogs','QueueManager','Users'],
      };
      Object.keys(map).forEach(k => { if (map[k].includes(name)) this.expanded[k] = true; });
    },
    isGroupActive(key) {
      const map = {
        accounts: ['Accounts','Proxies','Channels','AdminChannels','MediaLibrary','AccountHealth','ImageGenerator'],
        neuro:    ['Campaigns','CampaignCreate','CampaignEdit','ActionLog','Autopost','Repost','Digest','Warmup','ContentPlans','ContentPlanCreate','ContentPlanEdit','MassActions'],
        growth:   ['Parser','Personas','ChannelSearch','parser-channels','parser-groups','parser-history'],
        system:   ['Settings','SystemLogs','QueueManager','Users'],
      };
      return map[key]?.includes(this.$route.name) ?? false;
    },
    toggleTheme() {
      this.isLight = !this.isLight;
      localStorage.setItem('nc-theme', this.isLight ? 'light' : 'dark');
      this.applyTheme();
    },
    applyTheme() {
      document.documentElement.classList.toggle('theme-light', this.isLight);
    },
    handleLogout() {
      this.logout();
      this.$router.push('/login');
    },
  },
};
</script>

<style scoped>
/* ─── Sidebar shell ─────────────────────────────────── */
.sidebar {
  width: var(--nc-sidebar-width, 196px);
  min-width: var(--nc-sidebar-width, 196px);
  height: 100vh;
  position: sticky;
  top: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--nc-sidebar-bg);
  border-right: 1px solid var(--nc-sidebar-border);
  transition: width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s cubic-bezier(0.4,0,0.2,1);
  overflow: hidden;
  z-index: 200;
}
.sidebar.collapsed {
  width: 54px;
  min-width: 54px;
}

/* ─── Brand ─────────────────────────────────────────── */
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 12px 12px;
  border-bottom: 1px solid rgba(6, 182, 212, 0.08);
  position: relative;
  flex-shrink: 0;
}
.brand-icon {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background: var(--nc-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #020617;
  flex-shrink: 0;
  box-shadow: 0 0 14px rgba(6,182,212,.4);
}
.brand-name {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  line-height: 1.2;
}
.brand-text   { font-size: 13px; font-weight: 700; color: #e2e8f0; letter-spacing: -0.3px; }
.brand-accent { font-size: 13px; font-weight: 700; color: var(--nc-primary); letter-spacing: -0.3px; }
.brand-ver    { font-size: 9px; color: #334155; font-weight: 500; margin-top: 2px; }

.collapse-btn {
  margin-left: auto;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(255,255,255,.08);
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}
.collapse-btn:hover { background: rgba(255,255,255,.1); color: var(--nc-primary); border-color: rgba(6,182,212,.3); }

/* ─── Navigation ─────────────────────────────────────── */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.sidebar-nav::-webkit-scrollbar { width: 3px; }
.sidebar-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 10px; }

/* Direct nav item (dashboard) */
.nav-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 9px;
  border-radius: 4px;
  color: #64748b;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}
.nav-item:hover { background: rgba(255,255,255,.05); color: #e2e8f0; }
.nav-item.active {
  background: rgba(6,182,212,.1);
  color: var(--nc-primary);
  box-shadow: inset 2px 0 0 var(--nc-primary);
}

/* Group container */
.nav-group { display: flex; flex-direction: column; }

/* Group header button */
.nav-group-header {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 9px;
  border-radius: 4px;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  overflow: hidden;
}
.nav-group-header:hover { background: rgba(255,255,255,.05); color: #e2e8f0; }
.nav-group-header.group-active { color: #94a3b8; }

.nav-icon {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nav-group-label { flex: 1; }
.nav-chevron {
  margin-left: auto;
  color: #334155;
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.nav-chevron.open { transform: rotate(180deg); }

/* Subitems */
.nav-subitems {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding-left: 26px;
  padding-bottom: 3px;
}
.nav-subitem {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 9px;
  border-radius: 4px;
  color: #475569;
  text-decoration: none;
  font-size: 12.5px;
  font-weight: 400;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
}
.nav-subitem:hover { background: rgba(255,255,255,.04); color: #94a3b8; }
.nav-subitem.active { color: var(--nc-primary); background: rgba(6,182,212,.07); }
.nav-subitem.active .sub-dot { background: var(--nc-primary); box-shadow: 0 0 5px var(--nc-primary); }

.sub-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #2d3748;
  flex-shrink: 0;
  transition: background 0.15s;
}

/* ─── Theme toggle ──────────────────────────────────── */
.theme-toggle-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 14px;
}
.theme-toggle-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
  color: var(--nc-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.theme-toggle-btn:hover {
  background: var(--nc-primary-subtle);
  border-color: var(--nc-primary);
  color: var(--nc-primary);
}
.theme-label {
  font-size: 12px;
  color: var(--nc-text-muted);
  font-weight: 500;
}

/* ─── Footer ─────────────────────────────────────────── */
.sidebar-footer {
  padding: 10px 8px;
  border-top: 1px solid rgba(255,255,255,.05);
  flex-shrink: 0;
}
.user-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 6px;
  border-radius: 4px;
  transition: background 0.15s;
  overflow: hidden;
}
.user-row:hover { background: rgba(255,255,255,.04); }
.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--nc-primary) 0%, #0891b2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #020617;
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(6,182,212,.3);
}
.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}
.user-name { font-size: 12px; font-weight: 600; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-role { font-size: 10px; color: #334155; }
.logout-btn {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border: none;
  background: none;
  color: #334155;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.logout-btn:hover { color: var(--nc-danger); background: rgba(239,68,68,.1); }

/* ─── Animations ─────────────────────────────────────── */
.fade-label-enter-active,
.fade-label-leave-active { transition: opacity 0.15s, transform 0.15s; }
.fade-label-enter-from,
.fade-label-leave-to { opacity: 0; transform: translateX(-4px); }

.slide-down-enter-active { transition: max-height 0.22s ease, opacity 0.18s ease; max-height: 400px; overflow: hidden; }
.slide-down-leave-active { transition: max-height 0.18s ease, opacity 0.15s ease; max-height: 400px; overflow: hidden; }
.slide-down-enter-from,
.slide-down-leave-to { max-height: 0 !important; opacity: 0; }

/* ─── Mobile ─────────────────────────────────────────── */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: 0; top: 0; bottom: 0;
    transform: translateX(-100%);
    transition: transform 0.25s cubic-bezier(0.4,0,0.2,1);
    width: 220px !important;
    min-width: 220px !important;
    box-shadow: 4px 0 32px rgba(0,0,0,.6);
  }
  .sidebar.mobile-open { transform: translateX(0); }
  .d-none.d-md-flex { display: none !important; }
}
</style>
